import { useTeamStore } from '../../store/teamStore';

import React, { useState, useEffect } from 'react';
import { X, Check, Shield, Users, FileText, BarChart2, Settings, Lightbulb, Briefcase, Calendar, Image, PenTool, UserPlus } from 'lucide-react';
import { teamService } from '../../services/team.service';
import { toast } from 'sonner';

interface RolePermissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface PermissionCategory {
    id: string;
    name: string;
    icon: React.ElementType;
    permissions: {
        id: string;
        label: string;
        description: string;
    }[];
}

const permissionCategories: PermissionCategory[] = [
    {
        id: 'team',
        name: 'Team Management',
        icon: Users,
        permissions: [
            { id: 'invite_member', label: 'Invite Members', description: 'Can invite new members to the team' },
            { id: 'manage_roles', label: 'Manage Roles', description: 'Can change member roles and permissions' },
            { id: 'remove_member', label: 'Remove Members', description: 'Can remove members from the team' },
        ]
    },
    {
        id: 'content',
        name: 'Content Editor',
        icon: PenTool,
        permissions: [
            { id: 'create_content', label: 'Create Content', description: 'Can create new content drafts' },
            { id: 'edit_content', label: 'Edit Content', description: 'Can edit existing content' },
            { id: 'publish_content', label: 'Publish Content', description: 'Can publish content to platforms' },
            { id: 'review_content', label: 'Review Content', description: 'Can review and approve content' },
        ]
    },
    {
        id: 'ideavault',
        name: 'Idea Vault',
        icon: Lightbulb,
        permissions: [
            { id: 'create_idea', label: 'Create Ideas', description: 'Can add new ideas to the vault' },
            { id: 'edit_idea', label: 'Edit Ideas', description: 'Can edit existing ideas' },
            { id: 'delete_idea', label: 'Delete Ideas', description: 'Can remove ideas from the vault' },
            { id: 'view_ideas', label: 'View Ideas', description: 'Can view the idea vault' },
        ]
    },
    {
        id: 'projects',
        name: 'Projects',
        icon: Briefcase,
        permissions: [
            { id: 'create_project', label: 'Create Projects', description: 'Can create new projects' },
            { id: 'edit_project', label: 'Edit Projects', description: 'Can edit project details' },
            { id: 'delete_project', label: 'Delete Projects', description: 'Can delete projects' },
            { id: 'view_projects', label: 'View Projects', description: 'Can view all projects' },
        ]
    },
    {
        id: 'calendar',
        name: 'Calendar',
        icon: Calendar,
        permissions: [
            { id: 'view_calendar', label: 'View Calendar', description: 'Can view the content calendar' },
            { id: 'manage_events', label: 'Manage Events', description: 'Can add and edit calendar events' },
        ]
    },
    {
        id: 'partnerships',
        name: 'Partnerships',
        icon: UserPlus,
        permissions: [
            { id: 'manage_partnerships', label: 'Manage Partnerships', description: 'Can add and edit partnerships' },
            { id: 'view_partnerships', label: 'View Partnerships', description: 'Can view partnership details' },
        ]
    },
    {
        id: 'assets',
        name: 'Assets',
        icon: Image,
        permissions: [
            { id: 'upload_assets', label: 'Upload Assets', description: 'Can upload files to the library' },
            { id: 'delete_assets', label: 'Delete Assets', description: 'Can remove files from the library' },
            { id: 'view_assets', label: 'View Assets', description: 'Can view and download assets' },
        ]
    },
    {
        id: 'analytics',
        name: 'Analytics',
        icon: BarChart2,
        permissions: [
            { id: 'view_analytics', label: 'View Analytics', description: 'Can view performance metrics' },
            { id: 'export_reports', label: 'Export Reports', description: 'Can download analytics reports' },
        ]
    },
    {
        id: 'settings',
        name: 'Settings',
        icon: Settings,
        permissions: [
            { id: 'manage_settings', label: 'Manage Settings', description: 'Can modify workspace settings' },
            { id: 'billing_access', label: 'Billing Access', description: 'Can view and manage billing' },
        ]
    }
];

const roles = [
    { id: 'Admin', label: 'Admin', icon: Shield, description: 'Full access to all features' },
    { id: 'Editor', label: 'Editor', icon: FileText, description: 'Can create and edit content' },
    { id: 'Viewer', label: 'Viewer', icon: Users, description: 'Can view content and analytics' },
    { id: 'Analyst', label: 'Analyst', icon: BarChart2, description: 'Access to analytics only' },
];

const RolePermissionsModal: React.FC<RolePermissionsModalProps> = ({ isOpen, onClose }) => {
    const { fetchRolePermissions } = useTeamStore();
    const [activeRole, setActiveRole] = useState('Admin');
    const [rolePermissions, setRolePermissions] = useState<Record<string, string[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadPermissions();
        }
    }, [isOpen]);

    const loadPermissions = async () => {
        try {
            setIsLoading(true);
            const permissions = await teamService.getRolePermissions();
            setRolePermissions(permissions);
        } catch (error) {
            console.error('Failed to load permissions:', error);
            toast.error('Failed to load permissions');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePermissionToggle = (permissionId: string) => {
        if (activeRole === 'Admin') return; // Admin always has all permissions

        setRolePermissions(prev => {
            const currentRolePermissions = prev[activeRole] || [];
            const hasPermission = currentRolePermissions.includes(permissionId);

            let newPermissions;
            if (hasPermission) {
                newPermissions = currentRolePermissions.filter(id => id !== permissionId);
            } else {
                newPermissions = [...currentRolePermissions, permissionId];
            }

            return {
                ...prev,
                [activeRole]: newPermissions
            };
        });
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            // Save permissions for the active role (or all modified roles if we tracked changes differently)
            // Ideally we should save all, but the API is per role. 
            // For simplicity, let's just save the currently active role's permissions or iterate all.
            // Iterating all might be safer to ensure consistency.

            const promises = Object.entries(rolePermissions).map(([role, permissions]) => {
                // Skip Admin as it's usually immutable or handled specially on backend
                if (role === 'Admin') return Promise.resolve();
                return teamService.updateRolePermissions(role, permissions);
            });

            await Promise.all(promises);
            await fetchRolePermissions(); // Refresh permissions in store
            toast.success('Permissions saved successfully');
            onClose();
        } catch (error) {
            console.error('Failed to save permissions:', error);
            toast.error('Failed to save permissions');
        } finally {
            setIsSaving(false);
        }
    };

    const isPermissionEnabled = (permissionId: string) => {
        if (activeRole === 'Admin') return true;
        return (rolePermissions[activeRole] || []).includes(permissionId);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Roles & Permissions</h2>
                        <p className="text-gray-500 mt-1">Manage what each role can do in your workspace</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar - Roles */}
                    <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                        <div className="p-4 space-y-2">
                            {roles.map((role) => {
                                const Icon = role.icon;
                                return (
                                    <button
                                        key={role.id}
                                        onClick={() => setActiveRole(role.id)}
                                        className={`w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center gap-3 ${activeRole === role.id
                                            ? 'bg-white shadow-md ring-1 ring-gray-200'
                                            : 'hover:bg-gray-100 text-gray-600'
                                            }`}
                                    >
                                        <div className={`p-2 rounded-lg ${activeRole === role.id ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className={`font-semibold ${activeRole === role.id ? 'text-gray-900' : 'text-gray-700'}`}>
                                                {role.label}
                                            </div>
                                            <div className="text-xs text-gray-500 line-clamp-1">{role.description}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Main Content - Permissions */}
                    <div className="flex-1 overflow-y-auto bg-white">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                            </div>
                        ) : (
                            <div className="p-8">
                                <div className="mb-6 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                            {activeRole} Permissions
                                            {activeRole === 'Admin' && (
                                                <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                                                    Full Access
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-gray-500 text-sm mt-1">
                                            {activeRole === 'Admin'
                                                ? 'Admins have full access to all features and settings.'
                                                : `Configure what ${activeRole}s can access and manage.`}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    {permissionCategories.map((category) => {
                                        const CategoryIcon = category.icon;
                                        return (
                                            <div key={category.id} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="p-2 bg-white rounded-lg shadow-sm text-primary-600">
                                                        <CategoryIcon className="w-5 h-5" />
                                                    </div>
                                                    <h4 className="font-semibold text-gray-900">{category.name}</h4>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {category.permissions.map((permission) => (
                                                        <div
                                                            key={permission.id}
                                                            onClick={() => handlePermissionToggle(permission.id)}
                                                            className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${isPermissionEnabled(permission.id)
                                                                ? 'bg-white border-primary-200 shadow-sm'
                                                                : 'bg-transparent border-transparent hover:bg-gray-100'
                                                                } ${activeRole === 'Admin' ? 'cursor-default opacity-75' : ''}`}
                                                        >
                                                            <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-colors ${isPermissionEnabled(permission.id)
                                                                ? 'bg-primary-600 border-primary-600 text-white'
                                                                : 'border-gray-300 bg-white'
                                                                }`}>
                                                                {isPermissionEnabled(permission.id) && <Check className="w-3.5 h-3.5" />}
                                                            </div>
                                                            <div>
                                                                <div className={`text-sm font-medium ${isPermissionEnabled(permission.id) ? 'text-primary-900' : 'text-gray-700'
                                                                    }`}>
                                                                    {permission.label}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-0.5">
                                                                    {permission.description}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RolePermissionsModal;
